import { cloneElement, ComponentProps, isValidElement } from 'react';
import { css } from '@emotion/react';
import { InputState } from '@frontend/generic-dialpad-accessories';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { AutocompleteGroupItems, useDialerAutocomplete } from '../../hooks/use-dialer-autocomplete';
import { Autocomplete } from '.';

type AutocompleteItems = {
  [P in AutocompleteGroupItems]: string;
};

export type AutocompleteWrapsProps = {
  isDirectoryLoading: boolean;
  hideAutocomplete?: boolean;
  hideAutocompleteOnEmpty?: boolean;
  hideAutocompleteOnBlur?: boolean;
  inputState: InputState;
  currentDial: string | undefined;
  getItemProps: (index: number, action: () => void) => ComponentProps<'li'>;
};

export const AutocompleteWraps = ({
  isDirectoryLoading: isLoading,
  autocompleteItems,
  hideAutocomplete = false,
  hideAutocompleteOnEmpty = false,
  hideAutocompleteOnBlur = false,
  inputState,
  currentDial,
  getItemProps,
}: AutocompleteWrapsProps & ReturnType<typeof useDialerAutocomplete>) => {
  let counter = 0;
  const { t } = useTranslation('softphone');

  const groupHeader: AutocompleteItems = {
    no_group_name: '',
    contacts: t('Contacts'),
    last_called: t('Last Called'),
    most_contacted: t('Most Contacted'),
    extensions: t('Extensions'),
  };

  const showAutocomplete =
    ((hideAutocompleteOnBlur && inputState.focused) || !hideAutocompleteOnBlur) &&
    ((hideAutocompleteOnEmpty && currentDial?.length) || !hideAutocompleteOnEmpty) &&
    !hideAutocomplete;

  const isAutocompleteLoading = isLoading && !!currentDial;

  return (
    <Autocomplete isLoading={isAutocompleteLoading} show={!!showAutocomplete}>
      {!!autocompleteItems &&
        (Object.keys(autocompleteItems) as AutocompleteGroupItems[])
          .filter((group) => !!autocompleteItems[group]?.length)
          .map((groupName) => {
            if (!autocompleteItems[groupName].length) return null;
            return (
              <div key={groupName}>
                {groupName !== 'no_group_name' && (
                  <Text
                    css={css`
                      padding: ${theme.spacing(1, 2)};
                    `}
                    weight='bold'
                    color='white'
                  >
                    {groupHeader[groupName]}
                  </Text>
                )}
                {/*
                  These group items are created in a very distributed way, so there's no centralized location to spread the `getItemProps` function from usePopoverMenu.
                  We clone the element and spread the props here.
                */}
                {autocompleteItems[groupName].map((item) => {
                  if (isValidElement(item)) {
                    const { onClick, ...props } = getItemProps(counter++, item.props.onClick);

                    return cloneElement(item, props);
                  } else {
                    return item;
                  }
                })}
              </div>
            );
          })}
    </Autocomplete>
  );
};
